import AbstractChatConnector from "./abstractChatConnector";
import {AgentEvent, ChatType} from "../../../types/enums";
import {EventFactory} from "../../factory/eventFactory";
import {waitForSocketConnection} from "../../utils/socketUtils";
import {PingMessage, SessionTimeoutMessage} from "../../../types/chat";

export default class AgentChatConnector extends AbstractChatConnector
{
    private isConnectedMessageArrived: boolean = false;
    private disconnectTimerId: null|number = null;
    private pingTimerId: null|number = null;
    private defaultTimeout : number = 200;
    private maxTimeout : number = 310;

    public async updateDisconnectTimer( doneSeconds: null|number = null ):Promise<void>
    {
        if (this.disconnectTimerId !== null) {
            clearTimeout(this.disconnectTimerId);
        }

        let timeout = doneSeconds ? this.maxTimeout-doneSeconds : this.defaultTimeout;

        if (timeout <=0) {
            timeout = this.defaultTimeout;
        }

        this.disconnectTimerId = setTimeout(() => {
            this.sendConnectionTimeoutEvent();
        }, timeout * 1000);

    }
    public async startPing(  ):Promise<void>
    {
        if (this.pingTimerId !== null) {
            clearTimeout(this.pingTimerId);
        }
        this.pingTimerId = setInterval(() => {
            this.sendPing();
        }, 60000);
    }

    public stopTimer(): void {
        if (this.disconnectTimerId !== null) {
            clearTimeout(this.disconnectTimerId);
        }

        if (this.pingTimerId !== null) {
            clearTimeout(this.pingTimerId);
        }
    }

    protected async specialConnectionInitialisation( ):Promise<void>
    {
        this.isConnectedMessageArrived = false;
        await this.sendJoinEvent();
        await this.waitForConnectedEvent(600, 50);
        await this.startPing();
    }

    protected async registerOnMessageHandler( webSocket: WebSocket ):Promise<void>
    {
        webSocket.onmessage = (event: MessageEvent): void => {
            let data = JSON.parse(event.data);
            let responseType = data.type;

            if(responseType === "Event") {
                if (data.class === 'Connected' && data.code === 200) {
                    if (this.isConnectedMessageArrived) {
                        // weiteres connected-event beudeudet,
                        // dass eine weitere wss-connection in anderer App gestartet wurde
                        this.alpineComponent.minimizeChat();
                    }
                    this.isConnectedMessageArrived = true;
                    return;
                }
                this.eventHandler.handleOnEvent(data);
            } else if(responseType === "Response") {
                // aktuell nichts tun (nur pong ist ein response)
            } else {
                this.messageHandler.handleOnAgentMessage(data);
            }
        };
    }

    protected isValidFinishedInitConnectionState(): boolean {
        if(!super.isValidFinishedInitConnectionState()) {
            return false;
        }

        return this.isConnectedMessageArrived;
    }

    private async waitForConnectedEvent(maxAttempts: number, intervalMs: number): Promise<void> {
        for (let attempt = 0; attempt < maxAttempts; attempt++) {
            if (this.isConnectedMessageArrived) {
                await this.updateDisconnectTimer();
                return;
            }
            await new Promise(resolve => setTimeout(resolve, intervalMs));
        }

        this.eventHandler.handleSendEventError();
    }

    public getChatType( ): string
    {
        return ChatType.AGENT_CHAT;
    }

    private async sendJoinEvent(): Promise<void> {
        const userData= this.getUserData();
        if(userData === null) {
            return;
        }

        let agentChatId = this.componentConfig.getAgentChatId();
        let joinEventObject = EventFactory.createJoin(
            this.config.trademark,
            this.componentConfig.queue,
            agentChatId !== null ? agentChatId : '',
            userData
        );

        let webSocket = await this.getWebSocket();
        waitForSocketConnection(
            webSocket,
            this.eventHandler.handleSendJoinEvent,
            this.eventHandler.handleSendEventError,
            joinEventObject
        );
    }

    private async sendConnectionTimeoutEvent(): Promise<void> {
        let webSocket = await this.getWebSocket();
        if (webSocket === null) {
            return;
        }

        waitForSocketConnection(
            webSocket,
            this.eventHandler.handleSendConnectionTimeoutEvent,
            ():void => { },
            new SessionTimeoutMessage()
        );
    }

    public async sendPing(): Promise<void> {
        if (!this.isWebsocketAvailable()) {
            return;
        }

        let webSocket = await this.getWebSocket();
        if (webSocket === null) {
            return;
        }

        waitForSocketConnection(
            webSocket,
            this.eventHandler.handleSendPing,
            ():void => { },
            new PingMessage()
        );
    }
}